import React, { useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  Button,
  BackButton,
  TooltipText,
  SharableLink,
  OverlayLoader,
  NotFound,
  useToast,
} from 'components';
import { usePublishSession, useSession } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    height: '100vh',
    width: '100%',
    overflow: 'hidden',

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  iframe: {
    flex: 1,
    width: '100%',
    margin: 0,
    border: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',

    padding: `${theme.marginBase * 3}px ${theme.marginBase * 6}px`,
    backgroundColor: theme.colors.white,
    boxShadow: theme.boxShadow.base,
  },
  title: {
    ...theme.fonts.h4,
    marginLeft: theme.marginBase * 3,
  },
  help: {
    padding: theme.marginBase,
    paddingBottom: 4,
    fontSize: theme.marginBase * 3,
    color: theme.colors.inkSecondary,
  },
  spacer: {
    flex: 1,
  },
  centeredModalText: {
    ...theme.fonts.body,
    marginTop: theme.marginBase * 2,
  },
  centeredModalInfo: {
    ...theme.fonts.body,
    marginTop: theme.marginBase * 3,
    color: theme.colors.inkSecondary,
  },
  link: {
    marginTop: theme.marginBase * 3,
    margin: 'auto',
  },
}));

export const Preview = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const { id } = useParams();
  const { data: session, isLoading: fetching } = useSession(id);

  const [iFrameLoading, setIframeLoading] = useState(true);

  const onIframeLoaded = () => setTimeout(() => setIframeLoading(false), 500);

  const { mutate: publish, isLoading: publishing } = usePublishSession(id, {
    onSuccess: () => {
      toast.success(t('toast.session.published'));
      navigate(`/sessions/details/${id}`);
    },
    onError(e: any) {
      if (
        e.response &&
        e.response.data &&
        e.response.data.code === 'VALIDATION_FAILED'
      ) {
        toast.error(t('editSession.review.errors'));
      } else {
        toast.exception(e);
      }
    },
  });

  if (!fetching && !session) {
    return <NotFound />;
  }
  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <BackButton color="ink" />
        <h1 className={classes.title}>
          <Trans i18nKey="sessions.preview.title" />
        </h1>
        <TooltipText
          className={classes.help}
          title={t('sessions.preview.tooltip')}
        >
          <i className="ri-question-line" />
        </TooltipText>
        <div className={classes.spacer} />
        {session && !session.published && (
          <Button
            onClick={() => publish()}
            color="green"
            type="button"
            small
            isLoading={publishing}
          >
            <Trans i18nKey="editSession.buttons.publish" />
          </Button>
        )}
        {session && session.published && (
          <SharableLink
            link={`https://${window.location.host}/study/${session.id}`}
          />
        )}
      </div>
      <iframe
        src={`/study/${id}?preview=true`}
        className={classes.iframe}
        title="Preview"
        onLoad={onIframeLoaded}
      >
        {"Your browser doen't support the preview"}
      </iframe>
      {(fetching || iFrameLoading) && <OverlayLoader />}
    </div>
  );
};
